import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AdminFileRegistryItem,
  AdminFileRegistryListParams,
  AdminListResult,
  AdminQueueDashboard,
  AdminQueueHistoryPoint,
} from '../../../api/types';
import { formatFileSize } from '../../../utils/formatFileSize';
import { adminApi } from '../adminApi';

interface FileRegistryFilters {
  search: string;
  access_policy: '' | 'private' | 'public_image';
  lifecycle: '' | 'active' | 'deleting' | 'deleted';
  processing_status: '' | 'none' | 'pending' | 'processing' | 'failed' | 'done';
}

const defaultFileFilters: FileRegistryFilters = {
  search: '',
  access_policy: '',
  lifecycle: '',
  processing_status: '',
};

export function FileRegistryPage() {
  const [filters, setFilters] = useState<FileRegistryFilters>(defaultFileFilters);
  const [appliedFilters, setAppliedFilters] = useState<FileRegistryFilters>(defaultFileFilters);
  const [files, setFiles] = useState<AdminListResult<AdminFileRegistryItem>>({ items: [], meta: {} });
  const [expandedFileIds, setExpandedFileIds] = useState<Set<number>>(() => new Set());
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .getFileRegistry(buildFileRegistryParams(appliedFilters, page))
      .then((result) => {
        if (!ignore) {
          setFiles(result);
        }
      })
      .catch((error: Error) => {
        if (!ignore) {
          setErrorMessage(error.message);
        }
      })
      .finally(() => {
        if (!ignore) {
          setIsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [appliedFilters, page]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  const resetFilters = (): void => {
    setFilters(defaultFileFilters);
    setAppliedFilters(defaultFileFilters);
    setPage(1);
  };

  const toggleFile = (fileId: number): void => {
    setExpandedFileIds((currentIds) => {
      const nextIds = new Set(currentIds);

      if (nextIds.has(fileId)) {
        nextIds.delete(fileId);
      } else {
        nextIds.add(fileId);
      }

      return nextIds;
    });
  };

  const pageCount = Number(files.meta.page_count ?? 1);

  return (
    <section className="admin-page diagnostics-page">
      <DiagnosticsHeader
        eyebrow="Хранилище"
        title="Реестр файлов"
        description="Оригиналы, связи, версии изображений и результаты фоновой обработки в одном месте."
      />

      <form className="diagnostics-filter" onSubmit={handleSubmit}>
        <label className="admin-field diagnostics-filter__search">
          <span>Поиск</span>
          <input
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            placeholder="ID, имя или SHA-256"
            type="search"
            value={filters.search}
          />
        </label>
        <FilterSelect
          label="Доступ"
          onChange={(value) => setFilters({ ...filters, access_policy: value as FileRegistryFilters['access_policy'] })}
          options={[['private', 'Приватный'], ['public_image', 'Публичное изображение']]}
          value={filters.access_policy}
        />
        <FilterSelect
          label="Жизненный цикл"
          onChange={(value) => setFilters({ ...filters, lifecycle: value as FileRegistryFilters['lifecycle'] })}
          options={[['active', 'Активный'], ['deleting', 'Удаляется'], ['deleted', 'Удалён']]}
          value={filters.lifecycle}
        />
        <FilterSelect
          label="Обработка"
          onChange={(value) => setFilters({ ...filters, processing_status: value as FileRegistryFilters['processing_status'] })}
          options={[['none', 'Без задачи'], ['pending', 'В очереди'], ['processing', 'В работе'], ['failed', 'Ошибка'], ['done', 'Готово']]}
          value={filters.processing_status}
        />
        <button className="diagnostics-button diagnostics-button_primary" type="submit">Применить</button>
        <button className="diagnostics-button" onClick={resetFilters} type="button">Сбросить</button>
      </form>

      {errorMessage !== null ? <p className="admin-message admin-message_error">{errorMessage}</p> : null}
      {isLoading ? <p className="diagnostics-loading">Загружаем реестр…</p> : null}

      <div className="file-registry" aria-busy={isLoading}>
        {files.items.map((file) => (
          <FileRegistryCard
            file={file}
            isExpanded={expandedFileIds.has(file.file_id)}
            key={file.file_id}
            onToggle={() => toggleFile(file.file_id)}
          />
        ))}
      </div>

      {!isLoading && files.items.length === 0 ? <p className="admin-empty">Файлы не найдены.</p> : null}

      <div className="diagnostics-pagination" aria-label="Пагинация реестра">
        <span>Всего {Number(files.meta.total ?? 0)}</span>
        <div>
          <button disabled={page <= 1 || isLoading} onClick={() => setPage((value) => value - 1)} type="button">← Назад</button>
          <span>{page} / {pageCount}</span>
          <button disabled={page >= pageCount || isLoading} onClick={() => setPage((value) => value + 1)} type="button">Вперёд →</button>
        </div>
      </div>
    </section>
  );
}

export function QueueDashboardPage() {
  const [dashboard, setDashboard] = useState<AdminQueueDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadDashboard = useCallback((showLoader: boolean): void => {
    if (showLoader) {
      setIsLoading(true);
    }

    adminApi
      .getQueueDashboard()
      .then((result) => {
        setDashboard(result);
        setErrorMessage(null);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    let isActive = true;

    const refresh = (): void => {
      adminApi
        .getQueueDashboard()
        .then((result) => {
          if (isActive) {
            setDashboard(result);
            setErrorMessage(null);
          }
        })
        .catch((error: Error) => {
          if (isActive) {
            setErrorMessage(error.message);
          }
        })
        .finally(() => {
          if (isActive) {
            setIsLoading(false);
          }
        });
    };

    refresh();
    const intervalId = window.setInterval(refresh, 15000);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const totals = useMemo(() => {
    return (dashboard?.queues ?? []).reduce(
      (result, queue) => ({
        pending: result.pending + queue.pending,
        processing: result.processing + queue.processing,
        failed: result.failed + queue.failed,
        processed24h: result.processed24h + queue.processed_24h,
      }),
      { pending: 0, processing: 0, failed: 0, processed24h: 0 },
    );
  }, [dashboard]);

  return (
    <section className="admin-page diagnostics-page queue-dashboard">
      <div className="diagnostics-heading-row">
        <DiagnosticsHeader
          eyebrow="Наблюдаемость"
          title="Очереди и нагрузка"
          description="Автообновление каждые 15 секунд. Метрики читаются из надёжных DB-состояний задач."
        />
        <button className="diagnostics-button" disabled={isLoading} onClick={() => loadDashboard(true)} type="button">
          {isLoading ? 'Обновляем…' : 'Обновить сейчас'}
        </button>
      </div>

      {errorMessage !== null ? <p className="admin-message admin-message_error">{errorMessage}</p> : null}

      <div className="queue-kpi-grid">
        <QueueKpi label="В очереди" tone="pending" value={totals.pending} />
        <QueueKpi label="В работе" tone="processing" value={totals.processing} />
        <QueueKpi label="Ошибки" tone="failed" value={totals.failed} />
        <QueueKpi label="Обработано за 24 ч" tone="done" value={totals.processed24h} />
      </div>

      <section className="diagnostics-panel diagnostics-panel_chart">
        <div className="diagnostics-panel__heading">
          <div>
            <p className="diagnostics-kicker">Пропускная способность</p>
            <h3>Поступление и обработка · 24 часа</h3>
          </div>
          <ChartLegend />
        </div>
        <QueueThroughputChart points={dashboard?.history ?? []} />
      </section>

      <div className="queue-source-grid">
        {(dashboard?.queues ?? []).map((queue) => (
          <article className="queue-source-card" key={queue.queue_code}>
            <div className="queue-source-card__heading">
              <div>
                <p className="diagnostics-kicker">{queue.queue_code}</p>
                <h3>{queue.queue_name}</h3>
              </div>
              <StatusDot hasIssue={queue.failed > 0 || queue.stale_processing > 0} />
            </div>
            <dl className="queue-source-card__metrics">
              <Metric label="Ожидают" value={queue.pending} />
              <Metric label="В работе" value={queue.processing} />
              <Metric label="Ошибки" value={queue.failed} danger={queue.failed > 0} />
              <Metric label="Всего готово" value={queue.done} />
              <Metric label="Создано / 24 ч" value={queue.created_24h} />
              <Metric label="Готово / 24 ч" value={queue.processed_24h} />
              <Metric label="Повтор запланирован" value={queue.retry_scheduled} />
              <Metric label="Зависшие" value={queue.stale_processing} danger={queue.stale_processing > 0} />
              <Metric label="Старейшая задача" value={formatDuration(queue.oldest_pending_age_seconds)} />
              <Metric label="Среднее время" value={formatDuration(queue.average_processing_seconds_24h)} />
            </dl>
          </article>
        ))}
      </div>

      <section className="diagnostics-panel">
        <div className="diagnostics-panel__heading">
          <div>
            <p className="diagnostics-kicker">Последние сбои</p>
            <h3>Ошибки, требующие внимания</h3>
          </div>
          <span className="diagnostics-timestamp">
            Срез: {dashboard === null ? '—' : formatDateTime(dashboard.generated_at)}
          </span>
        </div>
        <div className="queue-failure-list">
          {(dashboard?.recent_failures ?? []).map((failure) => (
            <article className="queue-failure" key={`${failure.queue_code}-${failure.task_id}`}>
              <div className="queue-failure__meta">
                <strong>{failure.queue_name} · #{failure.task_id}</strong>
                <span>{formatDateTime(failure.updated_at)} · попыток: {failure.attempts}</span>
              </div>
              <p>{failure.last_error}</p>
              <span>{failure.next_attempt_at === null || failure.next_attempt_at === undefined ? 'Повтор не запланирован' : `Следующая попытка: ${formatDateTime(failure.next_attempt_at)}`}</span>
            </article>
          ))}
          {dashboard !== null && dashboard.recent_failures.length === 0 ? (
            <p className="diagnostics-ok">Активных ошибок нет.</p>
          ) : null}
        </div>
      </section>
    </section>
  );
}

function DiagnosticsHeader({ description, eyebrow, title }: { description: string; eyebrow: string; title: string }) {
  return (
    <header className="diagnostics-header">
      <p className="diagnostics-kicker">{eyebrow}</p>
      <h2>{title}</h2>
      <p>{description}</p>
    </header>
  );
}

function FilterSelect({ label, onChange, options, value }: { label: string; onChange: (value: string) => void; options: [string, string][]; value: string }) {
  return (
    <label className="admin-field">
      <span>{label}</span>
      <select onChange={(event) => onChange(event.target.value)} value={value}>
        <option value="">Все</option>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </label>
  );
}

function FileRegistryCard({ file, isExpanded, onToggle }: { file: AdminFileRegistryItem; isExpanded: boolean; onToggle: () => void }) {
  const displayName = file.file_public_name ?? file.file_original_name;
  const dimensions = file.file_image_width !== null && file.file_image_width !== undefined
    ? `${file.file_image_width} × ${file.file_image_height ?? '—'}`
    : 'Не определены';

  return (
    <article className={`file-registry-card${file.processing_status === 'failed' ? ' has-error' : ''}`}>
      <button aria-expanded={isExpanded} className="file-registry-card__summary" onClick={onToggle} type="button">
        <span className="file-registry-card__type" aria-hidden="true">{file.file_access_policy === 'public_image' ? 'IMG' : 'FILE'}</span>
        <span className="file-registry-card__identity">
          <strong>{displayName}</strong>
          <small>#{file.file_id} · {file.file_mime_type}</small>
        </span>
        <span><small>Оригинал</small><strong>{formatFileSize(file.file_size)}</strong></span>
        <span><small>Ресайзы</small><strong>{file.variant_count} · {formatFileSize(file.variant_total_size)}</strong></span>
        <span><small>Связи</small><strong>{file.link_count}</strong></span>
        <StatusBadge status={file.processing_status} />
        <span className="file-registry-card__chevron" aria-hidden="true">⌄</span>
      </button>
      {isExpanded ? (
        <div className="file-registry-card__details">
          <dl className="file-detail-grid">
            <Metric label="Размер изображения" value={dimensions} />
            <Metric label="Активная версия" value={file.file_image_active_version ?? '—'} />
            <Metric label="Запрошенная версия" value={file.file_image_requested_version ?? '—'} />
            <Metric label="Ревизия запроса" value={file.file_image_request_revision} />
            <Metric label="Попытки обработки" value={file.processing_attempts} />
            <Metric label="Жизненный цикл" value={file.file_lifecycle} />
            <Metric label="Назначение" value={file.file_upload_purpose ?? '—'} />
            <Metric label="Загрузил пользователь" value={`#${file.file_uploaded_by}`} />
          </dl>
          <div className="file-technical-grid">
            <TechnicalValue label="SHA-256" value={file.file_hash} />
            <TechnicalValue label="Хранилище" value={`${file.file_storage} / ${file.file_bucket ?? 'default'}`} />
            <TechnicalValue label="Путь оригинала" value={file.file_path} />
            <TechnicalValue label="Image UUID" value={file.file_image_id ?? '—'} />
          </div>
          {file.processing_last_error !== null && file.processing_last_error !== undefined ? (
            <p className="file-processing-error"><strong>Ошибка обработки:</strong> {file.processing_last_error}</p>
          ) : null}
          <div className="file-variants">
            <h4>Производные файлы</h4>
            {file.variants.length === 0 ? <p>Ресайзы ещё не созданы.</p> : (
              <div className="file-variant-grid">
                {file.variants.map((variant) => (
                  <article key={variant.variant_id}>
                    <strong>{variant.width} × {variant.height}</strong>
                    <span>v{variant.version} · {variant.format.toUpperCase()} · {formatFileSize(variant.size)}</span>
                    <code title={variant.object_key}>{variant.object_key}</code>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function StatusBadge({ status }: { status?: string | null }) {
  const labels: Record<string, string> = {
    pending: 'В очереди',
    processing: 'В работе',
    failed: 'Ошибка',
    done: 'Готово',
  };
  const normalizedStatus = status ?? 'none';

  return <span className={`diagnostics-status diagnostics-status_${normalizedStatus}`}>{labels[normalizedStatus] ?? 'Без обработки'}</span>;
}

function TechnicalValue({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><code title={value}>{value}</code></div>;
}

function QueueKpi({ label, tone, value }: { label: string; tone: string; value: number }) {
  return <article className={`queue-kpi queue-kpi_${tone}`}><span>{label}</span><strong>{value.toLocaleString('ru-RU')}</strong></article>;
}

function Metric({ danger = false, label, value }: { danger?: boolean; label: string; value: number | string }) {
  return <div className={danger ? 'is-danger' : undefined}><dt>{label}</dt><dd>{typeof value === 'number' ? value.toLocaleString('ru-RU') : value}</dd></div>;
}

function StatusDot({ hasIssue }: { hasIssue: boolean }) {
  return <span className={`queue-health${hasIssue ? ' has-issue' : ''}`} title={hasIssue ? 'Есть проблемы' : 'Работает штатно'} />;
}

function ChartLegend() {
  return <div className="queue-chart-legend"><span className="created">Создано</span><span className="processed">Обработано</span><span className="failed">Ошибки</span></div>;
}

function QueueThroughputChart({ points }: { points: AdminQueueHistoryPoint[] }) {
  const width = 1000;
  const height = 260;
  const padding = 28;
  const maxValue = Math.max(1, ...points.flatMap((point) => [point.created, point.processed, point.failed]));
  const xStep = points.length > 1 ? (width - padding * 2) / (points.length - 1) : 0;
  const createPolyline = (key: 'created' | 'processed' | 'failed'): string => points.map((point, index) => {
    const x = padding + index * xStep;
    const y = height - padding - (point[key] / maxValue) * (height - padding * 2);

    return `${x},${y}`;
  }).join(' ');

  if (points.length === 0) {
    return <p className="diagnostics-empty-chart">Нет данных за последние 24 часа.</p>;
  }

  return (
    <div className="queue-chart-wrap">
      <svg aria-labelledby="queue-chart-title queue-chart-description" className="queue-chart" role="img" viewBox={`0 0 ${width} ${height}`}>
        <title id="queue-chart-title">Поступление и обработка задач за 24 часа</title>
        <desc id="queue-chart-description">Линейный график созданных, обработанных и ошибочных задач по часам.</desc>
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding + ratio * (height - padding * 2);

          return <line className="queue-chart__grid" key={ratio} x1={padding} x2={width - padding} y1={y} y2={y} />;
        })}
        <polyline className="queue-chart__line queue-chart__line_created" points={createPolyline('created')} />
        <polyline className="queue-chart__line queue-chart__line_processed" points={createPolyline('processed')} />
        <polyline className="queue-chart__line queue-chart__line_failed" points={createPolyline('failed')} />
      </svg>
      <div className="queue-chart__axis"><span>{formatHour(points[0].bucket_at)}</span><span>−12 ч</span><span>{formatHour(points[points.length - 1].bucket_at)}</span></div>
    </div>
  );
}

function buildFileRegistryParams(filters: FileRegistryFilters, page: number): AdminFileRegistryListParams {
  return {
    search: filters.search.trim() || undefined,
    access_policy: filters.access_policy || undefined,
    lifecycle: filters.lifecycle || undefined,
    processing_status: filters.processing_status || undefined,
    page,
    per_page: 25,
  };
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)} сек`;
  }

  if (seconds < 3600) {
    return `${Math.round(seconds / 60)} мин`;
  }

  return `${(seconds / 3600).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ч`;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(value));
}

function formatHour(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}
